import { useFormContext } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldCheck } from 'lucide-react';
import { BusinessQuoteFormSchema } from './schema';
import AddressFields from '../AddressFields';

export default function BusinessFields() {
	const {
		register,
		formState: { errors },
		setValue,
		watch,
	} = useFormContext<BusinessQuoteFormSchema>();

	const privacyAgreed = watch('privacy_agreed');
	const serviceCategory = watch('service_category');

	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-2xl font-bold font-heading text-blue-dark mb-2">Uw gegevens</h3>
				<p className="text-lg text-gray-dark mb-6">Laat uw gegevens achter, dan nemen wij zo snel mogelijk contact met u op.</p>
			</div>

			<div className="space-y-3">
				<Label htmlFor="business_name" className="text-lg">
					Bedrijfsnaam <span className="text-red-500">*</span>
				</Label>
				<Input
					id="business_name"
					placeholder="Uw bedrijfsnaam"
					className={errors.business_name ? 'border-red-500 h-12 text-lg md:text-lg' : 'h-12 text-lg md:text-lg'}
					{...register('business_name')}
				/>
				{errors.business_name && <p className="text-sm text-red-500">{errors.business_name.message}</p>}
			</div>

			<div className="space-y-3">
				<Label htmlFor="service_category" className="text-lg">
					Type dienst <span className="text-gray-400 font-normal">(optioneel)</span>
				</Label>
				<Select
					onValueChange={(val: 'vrf' | 'serverruimte' | 'winkel_horeca') =>
						setValue('service_category', val, { shouldValidate: true })
					}
					value={serviceCategory}
				>
					<SelectTrigger id="service_category" className="h-12 text-lg md:text-lg">
						<SelectValue placeholder="Selecteer type dienst" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="vrf" className="text-lg py-3">
							VRF-systemen / klimaatbeheersing
						</SelectItem>
						<SelectItem value="serverruimte" className="text-lg py-3">
							Serverruimtekoeling
						</SelectItem>
						<SelectItem value="winkel_horeca" className="text-lg py-3">
							Winkel / horeca
						</SelectItem>
					</SelectContent>
				</Select>
			</div>

			<AddressFields />

			<div className="space-y-3">
				<Label htmlFor="contact_email" className="text-lg">
					E-mailadres <span className="text-red-500">*</span>
				</Label>
				<Input
					id="contact_email"
					type="email"
					placeholder="uw@bedrijf.nl"
					className={errors.contact_email ? 'border-red-500 h-12 text-lg md:text-lg' : 'h-12 text-lg md:text-lg'}
					{...register('contact_email')}
				/>
				{errors.contact_email && <p className="text-sm text-red-500">{errors.contact_email.message}</p>}
			</div>

			<div className="space-y-3">
				<Label htmlFor="contact_phone" className="text-lg">
					Telefoonnummer <span className="text-red-500">*</span>
				</Label>
				<Input
					id="contact_phone"
					type="tel"
					placeholder="Direct nummer"
					className={errors.contact_phone ? 'border-red-500 h-12 text-lg md:text-lg' : 'h-12 text-lg md:text-lg'}
					{...register('contact_phone')}
				/>
				{errors.contact_phone && <p className="text-sm text-red-500">{errors.contact_phone.message}</p>}
				<p className="text-sm text-gray-500">Zodat we u kunnen terugbellen voor uw aanvraag.</p>
			</div>

			<div className="space-y-3">
				<Label htmlFor="location_remarks" className="text-lg">
					Opmerking of vraag <span className="text-gray-400 font-normal">(optioneel)</span>
				</Label>
				<Textarea
					id="location_remarks"
					placeholder="Waar kunnen we u mee helpen?"
					className="min-h-[120px] text-lg md:text-lg p-3"
					{...register('location_remarks')}
				/>
			</div>

			<div className="flex items-start space-x-3 pt-6 border-t border-gray-200">
				<Checkbox
					id="privacy_agreed"
					checked={privacyAgreed}
					onCheckedChange={(checked) => setValue('privacy_agreed', checked === true, { shouldValidate: true })}
					className={errors.privacy_agreed ? 'border-red-500 size-5 mt-1' : 'size-5 mt-1 border-gray-400'}
				/>
				<div className="grid gap-1.5 leading-none">
					<Label
						htmlFor="privacy_agreed"
						className="text-lg font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
					>
						Ik ga akkoord met de privacyverklaring <span className="text-red-500">*</span>
					</Label>
					<p className="text-sm text-gray-500">
						Wij gebruiken uw gegevens alleen om contact met u op te nemen over uw aanvraag.
					</p>
					{errors.privacy_agreed && <p className="text-sm text-red-500">{errors.privacy_agreed.message}</p>}
				</div>
			</div>

			<div className="flex items-center gap-2 mt-4 text-sm font-medium text-green-700 bg-green-50 p-3 rounded-md border border-green-100">
				<ShieldCheck className="size-5" />
				<span>Binnen enkele werkdagen persoonlijk antwoord van onze experts</span>
			</div>
		</div>
	);
}
